import _ from 'lodash';
import {connect} from 'react-redux';

import {FrameTypesEnum} from '../../../../../libs/shared/core/src/models/api/apiTypes';
import {getIsUserGlobalAdmin} from '../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {getHasFluSetupGuide} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {compareName} from '../../../../../srcCommon/helpers/localeHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {CollapsedItemKeyPrefixesEnum} from '../../../../models/api/localPreferencesModel';
import {fetchApplicationsAsyncAction} from '../../../../store/data/applications/applicationsActions';
import {getApplicationsAsyncStatus} from '../../../../store/data/applications/applicationsSelectors';
import {fetchIntegrationTypesAsyncAction} from '../../../../store/data/integrationTypes/integrationTypesActions';
import {
  getIntegrationTypesAsyncStatus,
  getPluginIntegrationTypes,
} from '../../../../store/data/integrationTypes/integrationTypesSelectors';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {toggleFrameIdAction} from '../../../../store/data/preferences/preferencesActions';
import {getSortedFrameIds} from '../../../../store/stateless/frameIdSelectors';
import {getSidePanelFrames, SidePanelFrame} from '../../../../store/stateless/sidePanelFrameSelectors';
import {SidePanelApplicationsManager} from './sidePanelApplicationsManager';

/*
 * Constants.
 */

const infoBoxExpandedKey = `${CollapsedItemKeyPrefixesEnum.P4_APPLICATIONS_MANAGER}infoBox`;
const isInfoBoxExpandedDefault = true;

/*
 * Selectors.
 */

const getIsInfoBoxExpanded = createSelector([getFindIsExpanded], (findIsExpanded) =>
  findIsExpanded(infoBoxExpandedKey, isInfoBoxExpandedDefault),
);

const getIsFramePinned = createSelector([getSortedFrameIds], (sortedFrameIds) => {
  const sortedFrameIdsSet = new Set(sortedFrameIds);
  return (frame: SidePanelFrame) => sortedFrameIdsSet.has(frame.frameId);
});

// This is only used for sorting, we will be translating these when we render the name.
const getFramesWithNameDefaults = createSelector(
  [getSidePanelFrames, getIsUserGlobalAdmin, getHasFluSetupGuide],
  (frames, isUserGlobalAdmin, hasFluSetupGuide) =>
    _([...frames])
      .map((frame) => {
        switch (frame.frameType) {
          case FrameTypesEnum.CALENDAR:
            return {
              ...frame,
              name: 'calendar',
            };
          case FrameTypesEnum.CUSTOMER_VIEW:
            return {
              ...frame,
              name: 'contacts',
            };
          case FrameTypesEnum.KEYBOARD_HELP:
            return {
              ...frame,
              name: 'shortcuts',
            };
          case FrameTypesEnum.START_GUIDE:
            return !(isUserGlobalAdmin || hasFluSetupGuide)
              ? {
                  ...frame,
                  name: 'help & tips',
                }
              : undefined;
          case FrameTypesEnum.KNOWLEDGE_BASE:
            return {
              ...frame,
              name: 'knowledge base',
            };
          default:
            return frame;
        }
      })
      .compact()
      .value(),
);

const getPinnedFrames = createSelector(
  [getFramesWithNameDefaults, getIsFramePinned],
  (frames, isFramePinned) =>
    _([...frames])
      .filter((f) => isFramePinned(f))
      .sort((frame1, frame2) => compareName(frame1.name.toLowerCase(), frame2.name.toLowerCase()))
      .value(),
);

const getUnpinnedFrames = createSelector(
  [getFramesWithNameDefaults, getIsFramePinned],
  (frames, isFramePinned) =>
    _([...frames])
      .filter((f) => !isFramePinned(f))
      .sort((frame1, frame2) => compareName(frame1.name.toLowerCase(), frame2.name.toLowerCase()))
      .value(),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  isInfoBoxExpanded: getIsInfoBoxExpanded,
  pinnedFrames: getPinnedFrames,
  unpinnedFrames: getUnpinnedFrames,
  integrationTypes: getPluginIntegrationTypes,
  integrationTypesAsyncStatus: getIntegrationTypesAsyncStatus,
  applicationsAsyncStatus: getApplicationsAsyncStatus,
}));

const dispatchProps = makeMapDispatchToProps()(() => ({
  onToggleInfoBox: () => toggleExpandItemAction(infoBoxExpandedKey, isInfoBoxExpandedDefault),
  onTogglePinned: (frame: SidePanelFrame) => toggleFrameIdAction(frame.frameId),
  fetchIntegrationTypesAsync: fetchIntegrationTypesAsyncAction,
  fetchApplicationsAsync: fetchApplicationsAsyncAction,
}));

export const SidePanelApplicationsManagerContainer = connect(
  mapStateToProps,
  dispatchProps,
)(SidePanelApplicationsManager);
